/**
 * Per-channel dataset card on the Datasets tab.
 *
 * Header (always visible) — channel colour bar, name, sub-meta,
 * 3-stat strip (golden / awaiting / IAA, last two conditional), chevron.
 * Below the header — stacked progress bar (when records > 0) + optional
 * train/eval sliver (when split + finalised > 0).
 * Expanded — `ChannelDatasetDetail`.
 *
 * "Only one card expanded at a time" is enforced by the parent
 * (`DatasetsTabContent`) via the `isExpanded` / `onToggle` props.
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';

import { Card } from '../../../../../ui/card';
import { cn } from '../../../../../../lib/utils';
import { CHANNELS } from '../../../shared/channels';
import { getIaaTone, IAA_TONE_TEXT_CLASS } from '../../../shared/iaa';
import type { ChannelDataset, GoLabDatasetRecordStatus } from '../../types';
import StackedSegmentBar from '../bars/StackedSegmentBar';
import SplitBar from '../bars/SplitBar';
import ChannelDatasetDetail from './ChannelDatasetDetail';
import ChannelRecordsModal from './ChannelRecordsModal';

interface Props {
  dataset: ChannelDataset;
  /**
   * Collection taxonomy context, threaded down to the records drill-down
   * modal (C7-FE) so an expanded record's labels resolve to human-readable
   * names. The card itself doesn't use them.
   */
  taxonomyKey: string;
  taxonomyVersionId: string;
  isExpanded: boolean;
  onToggle: () => void;
}

// B16-FE: clickable-stat → modal filter mapping.
export type ChannelStatFilterKey = 'golden' | 'disputed' | 'in-progress';

// The modal must show EXACTLY the records that contribute to the
// label the user clicked. The displayed "in-progress" number is
// `stats.annotated - stats.finalised`. Per the BE
// `_aggregate_channel_stats_for_collection.annotated_predicate`:
//   stats.annotated = ANNOTATED_FIRST + ASSIGNED_SECOND + ANNOTATED_BOTH
//                     + DISPUTED + AGREED + FINALISED
//   stats.finalised = FINALISED   (gold-pool only)
// So the displayed number rolls up five statuses; the filter must match.
//
// `assigned_first` is intentionally NOT in `annotated` (the first pass
// hasn't been submitted yet — claiming alone isn't progress). But
// `assigned_second` IS: the first pass has been completed, the record
// is waiting on the second annotator to claim/submit.
//
// Why we include DISPUTED here even though it has its own stat tile:
// `stats.annotated - stats.finalised` includes disputed records, so the
// modal must too. The same disputed record is reachable via two lenses
// (in-progress = "still in the workflow"; disputed = "needs adjudication"
// specifically). Same record, two valid filters.
const STAT_FILTERS: Record<ChannelStatFilterKey, GoLabDatasetRecordStatus[]> = {
  golden: ['finalised'],
  disputed: ['disputed'],
  'in-progress': [
    'annotated_first',
    'assigned_second',
    'annotated_both',
    'disputed',
    'agreed',
  ],
};

const STAT_TITLES: Record<ChannelStatFilterKey, string> = {
  golden: 'Golden records',
  disputed: 'Disputed records',
  'in-progress': 'In-progress records',
};

const ChannelDatasetCard: React.FC<Props> = ({
  dataset,
  taxonomyKey,
  taxonomyVersionId,
  isExpanded,
  onToggle,
}) => {
  const channel = CHANNELS[dataset.channelKey];
  const { stats, uploadCount, split } = dataset;
  const golden = stats.finalised;
  // Floor at zero — defensive against backend data anomalies where
  // `finalised > annotated` or `annotated > totalRecords` would otherwise
  // produce negative segments that skew the `StackedSegmentBar` math.
  const inProgress = Math.max(0, stats.annotated - stats.finalised);
  const awaiting = Math.max(0, stats.totalRecords - stats.annotated);
  const showAwaiting = awaiting > 0;
  const showDisputed = stats.disputed > 0;
  const showInProgress = inProgress > 0;
  const showIaa = stats.iaa !== null && stats.iaa > 0;
  const iaaTone = getIaaTone(stats.iaa);
  const showSplit = split !== null && stats.finalised > 0;

  // B16-FE: which stat-tile filter (if any) is currently driving the
  // open drill-down modal. `null` means closed.
  const [activeFilter, setActiveFilter] =
    React.useState<ChannelStatFilterKey | null>(null);
  const handleStatClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    key: ChannelStatFilterKey,
  ) => {
    // The outer trigger is a `role="button"` div (not a <button>) so
    // the nested stat <button>s are valid HTML. We still stopPropagation
    // so a stat-tile click doesn't bubble into the div-level onClick
    // and toggle the card.
    e.stopPropagation();
    setActiveFilter(key);
  };
  // Keyboard handler that emulates native <button> semantics on the
  // trigger div - Enter and Space both fire the toggle. Space's
  // default behaviour (page scroll) is suppressed since the div has
  // tabIndex=0 and the user expects button-like behaviour.
  const handleTriggerKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    // Ignore key events that originated on a focused descendant (e.g. a
    // stat-tile <button>). Without this guard, pressing Enter on a
    // stat button would both fire the stat's click handler AND bubble
    // into here and toggle the card.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`channel-card-${dataset.channelKey}`}>
      {/* Trigger is a `role="button"` div (not a <button>) so the
          nested stat-tile <button>s aren't invalid interactive-in-
          interactive HTML. Explicit Enter/Space handling on the div
          preserves the button-like UX. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={isExpanded}
        aria-controls={`channel-detail-${dataset.id}`}
        className="flex w-full cursor-pointer items-center gap-4 p-4 text-left transition hover:bg-muted/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        data-testid={`channel-card-trigger-${dataset.channelKey}`}
      >
        <div
          className="h-10 w-1 shrink-0 rounded"
          style={{ backgroundColor: channel.color }}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">{channel.name}</div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">
            {stats.totalRecords.toLocaleString()} records · {uploadCount} upload{uploadCount === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex items-center gap-5 text-center">
          {/* Stat-tile colours come from `--go-*` design tokens (same
              palette as `ui/badge.tsx`) rather than raw Tailwind
              colour ramps, so a brand re-skin can swap tokens once
              instead of grepping every component. */}
          <div data-testid="stat-golden">
            {golden > 0 ? (
              <button
                type="button"
                onClick={(e) => handleStatClick(e, 'golden')}
                className="-mx-1 rounded px-1 text-sm font-semibold tabular-nums text-[var(--go-success)] hover:bg-[var(--go-success-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--go-success)]"
                aria-label={`View ${golden.toLocaleString()} golden records`}
                data-testid="stat-golden-button"
              >
                {golden.toLocaleString()}
              </button>
            ) : (
              // Mirror the disputed / in-progress tiles' gating — a
              // zero value renders as a non-clickable number so a click
              // doesn't open an empty drill-down modal.
              <span
                className="-mx-1 px-1 text-sm font-semibold tabular-nums text-muted-foreground"
                data-testid="stat-golden-button"
              >
                {golden.toLocaleString()}
              </span>
            )}
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              golden
            </div>
          </div>
          {showDisputed && (
            <div data-testid="stat-disputed">
              <button
                type="button"
                onClick={(e) => handleStatClick(e, 'disputed')}
                className="-mx-1 rounded px-1 text-sm font-semibold tabular-nums text-[var(--go-berry)] hover:bg-[var(--go-berry-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--go-berry)]"
                aria-label={`View ${stats.disputed.toLocaleString()} disputed records`}
                data-testid="stat-disputed-button"
              >
                {stats.disputed.toLocaleString()}
              </button>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                disputed
              </div>
            </div>
          )}
          {showInProgress && (
            <div data-testid="stat-in-progress">
              <button
                type="button"
                onClick={(e) => handleStatClick(e, 'in-progress')}
                className="-mx-1 rounded px-1 text-sm font-semibold tabular-nums text-[var(--go-bittersweet)] hover:bg-[var(--go-bittersweet-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--go-bittersweet)]"
                aria-label={`View ${inProgress.toLocaleString()} in-progress records`}
                data-testid="stat-in-progress-button"
              >
                {inProgress.toLocaleString()}
              </button>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                in progress
              </div>
            </div>
          )}
          {showAwaiting && (
            <div data-testid="stat-awaiting">
              <div className="text-sm font-semibold tabular-nums text-muted-foreground">
                {awaiting.toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                awaiting
              </div>
            </div>
          )}
          {showIaa && stats.iaa !== null && (
            <div data-testid="stat-iaa" data-tone={iaaTone}>
              <div
                className={cn(
                  'text-sm font-semibold tabular-nums',
                  IAA_TONE_TEXT_CLASS[iaaTone],
                )}
              >
                {Math.round(stats.iaa * 100)}%
              </div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                IAA
              </div>
            </div>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            isExpanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </div>
      {stats.totalRecords > 0 && (
        <div className="px-4 pb-2">
          <StackedSegmentBar
            golden={golden}
            inProgress={inProgress}
            awaiting={awaiting}
          />
        </div>
      )}
      {showSplit && split && (
        <div className="px-4 pb-3">
          <SplitBar training={split.training} evaluation={split.evaluation} size="sm" />
        </div>
      )}
      {isExpanded && (
        <div id={`channel-detail-${dataset.id}`}>
          <ChannelDatasetDetail dataset={dataset} />
        </div>
      )}
      {/* B16-FE: drill-down modal. Conditional render → unmount on
          close resets `offset` state cleanly. Radix Dialog portals
          the actual content out of this DOM subtree anyway. */}
      {activeFilter !== null && (
        <ChannelRecordsModal
          open={activeFilter !== null}
          onOpenChange={(o) => {
            if (!o) setActiveFilter(null);
          }}
          channelDatasetId={dataset.id}
          channelName={channel.name}
          taxonomyKey={taxonomyKey}
          taxonomyVersionId={taxonomyVersionId}
          filterKey={activeFilter}
          statuses={STAT_FILTERS[activeFilter]}
          title={STAT_TITLES[activeFilter]}
        />
      )}
    </Card>
  );
};

export default ChannelDatasetCard;
